import React from 'react';
import { Check, Star, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const Pricing = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pricingPlans = [
    {
      title: 'Weekday',
      subtitle: 'Monday to Thursday',
      featured: false,
      prices: [
        { label: 'Full Day Hire', amount: '£100', period: 'per day' },
        { label: 'Half Day Hire', amount: '£75', period: 'max 4 hours' }
      ],
      features: [
        '300 miles per day included',
        'Flexible booking times',
        '£50 prepayment to secure booking',
        '£200 refundable security deposit',
        'Full insurance coverage'
      ]
    },
    {
      title: 'Weekend Special',
      subtitle: 'Saturday & Sunday',
      featured: true,
      prices: [
        { label: 'Full Weekend', amount: '£225', period: 'Sat & Sun', highlight: true }
      ],
      features: [
        'Best value for weekend trips',
        '600 miles included',
        'Two full days of hire',
        '£50 prepayment to secure booking',
        '£200 refundable security deposit',
        'Full insurance coverage',
        'Perfect for competitions'
      ]
    },
    {
      title: 'Weekend/Bank Holiday',
      subtitle: 'Friday to Sunday & Bank Holidays',
      featured: false,
      prices: [
        { label: 'Full Day Hire', amount: '£125', period: 'per day' }
      ],
      features: [
        '300 miles per day included',
        'Weekend & bank holiday availability',
        '£50 prepayment to secure booking',
        '£200 refundable security deposit',
        'Full insurance coverage',
        'Half day not available'
      ],
      note: 'Half day hire not available at weekends or on Bank Holidays'
    }
  ];

  return (
    <section id="pricing" className="py-20 lg:py-28 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 font-['Playfair_Display']">
            Simple, <span className="text-[#2d5f3f]">Transparent Pricing</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Competitive rates for premium horsebox hire. All payments must be made by bank transfer.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {pricingPlans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-3xl p-8 transition-all duration-300 ${
                plan.featured
                  ? 'bg-[#2d5f3f] text-white shadow-2xl scale-105 border-4 border-[#c8a882]'
                  : 'bg-white border-2 border-gray-200 hover:border-[#2d5f3f]/30 hover:shadow-xl'
              }`}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-[#c8a882] text-white px-6 py-2 text-sm font-semibold">
                    <Star className="w-4 h-4 mr-1 inline" />
                    Best Value
                  </Badge>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className={`text-2xl font-bold mb-2 ${
                  plan.featured ? 'text-white' : 'text-gray-900'
                }`}>
                  {plan.title}
                </h3>
                <p className={`text-sm ${
                  plan.featured ? 'text-white/80' : 'text-gray-500'
                }`}>
                  {plan.subtitle}
                </p>
              </div>

              {/* Prices */}
              <div className="mb-6 space-y-3">
                {plan.prices.map((price, idx) => (
                  <div key={idx} className={`text-center py-4 rounded-xl ${
                    price.highlight
                      ? 'bg-white/20 backdrop-blur-sm border-2 border-white/30'
                      : plan.featured
                      ? 'bg-white/10'
                      : 'bg-gray-50'
                  }`}>
                    <p className={`text-sm mb-1 ${
                      plan.featured ? 'text-white/80' : 'text-gray-600'
                    }`}>
                      {price.label}
                    </p>
                    <div className="flex items-baseline justify-center">
                      <span className={`text-4xl font-bold ${
                        plan.featured ? 'text-white' : 'text-[#2d5f3f]'
                      }`}>
                        {price.amount}
                      </span>
                      <span className={`ml-2 text-sm ${
                        plan.featured ? 'text-white/70' : 'text-gray-500'
                      }`}>
                        {price.period}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                      plan.featured ? 'text-[#c8a882]' : 'text-[#2d5f3f]'
                    }`} />
                    <span className={`text-sm ${
                      plan.featured ? 'text-white/90' : 'text-gray-600'
                    }`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {plan.note && (
                <p className={`text-xs mb-6 p-3 rounded-lg ${
                  plan.featured ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'
                }`}>
                  Note: {plan.note}
                </p>
              )}

              <Button
                onClick={scrollToContact}
                className={`w-full py-6 text-lg font-semibold transition-all duration-300 ${
                  plan.featured
                    ? 'bg-white text-[#2d5f3f] hover:bg-gray-100 hover:scale-105'
                    : 'bg-[#2d5f3f] text-white hover:bg-[#1e4029] hover:scale-105'
                }`}
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Enquire
              </Button>
            </div>
          ))}
        </div>

        {/* Payment Info */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-3">💳</span>
            Payment Information
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-gray-600">
            <div>
              <p className="font-semibold text-gray-900 mb-2">£50 Prepayment Required</p>
              <p className="text-sm">To secure your booking, a £50 prepayment is required.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-2">£200 Security Deposit</p>
              <p className="text-sm">Refundable deposit returned within 24 hours after safe return.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-2">Bank Transfer Only</p>
              <p className="text-sm">All payments must be made by bank transfer.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-2">Terms & Conditions</p>
              <p className="text-sm">
                <a href="/terms-and-conditions.pdf" target="_blank" rel="noopener noreferrer" className="text-[#2d5f3f] hover:underline">
                  View full terms and conditions
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;